import { auth } from '@/features/AuthProvider/AuthProvider';
import { RETAIL_API_KEY } from 'src/utils/envConfig';

interface FetchOptions {
  method?: string;
  headers?: object;
  body?: any;
  // Other options as needed
}

export class FetchError extends Error {
  constructor(
    public status: number,
    message: string,
    public info?: any,
  ) {
    super(message);
  }
}

export const fetcher = async <T>(
  url: string,
  options?: FetchOptions,
): Promise<T> => {
  const token = await auth.currentUser?.getIdToken();

  const response = await fetch(`${url}`, {
    ...options,
    headers: {
      'Content-Type': 'application/json',
      ...options?.headers,
      'x-trihow-api-key': RETAIL_API_KEY ?? '',
      'x-trihow-auth-token': token ?? '',
    },
  });

  const contentType = response.headers.get('Content-Type');
  const isJSON = contentType && contentType.includes('application/json');
  const isText = contentType && contentType.includes('text/html');
  const isBlob =
    contentType && contentType.includes('application/octet-stream');

  if (!response.ok) {
    let errorBody: any;
    if (isJSON) {
      try {
        errorBody = await response.json();
      } catch {
        // ignore JSON parse errors
      }
    }
    console.error(`Failed to fetch ${url}: ${response.statusText}`);
    throw new FetchError(
      response.status,
      `Failed to fetch ${url}: ${response.statusText}`,
      errorBody,
    );
  }

  if (isBlob) {
    const blob = await response.blob();
    return new Promise<T>((resolve) => {
      const reader = new FileReader();
      reader.onloadend = () => {
        const result = reader.result as string;
        resolve(JSON.parse(result));
      };
      reader.readAsText(blob);
    });
  }

  if (isJSON) {
    const result = await response.json();
    return result;
  }

  if (isText) {
    return response.text() as Promise<T>;
  }

  throw new Error(`Unsupported content type: ${contentType}`);
};
